import React, { useState, useEffect } from 'react';
import { UserLayout } from '../../components/user/UserLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  DollarSign, Layers, Building2, Loader2, AlertCircle, RefreshCw,
  Users, TrendingUp, ShoppingCart, XCircle, ChefHat, Wine,
  Award, BarChart2, Tag, ArrowUpDown
} from 'lucide-react';
import axios from 'axios';
import { useToast } from '../../hooks/use-toast';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const fmt = (n) => `$${(n || 0).toFixed(2)}`;

const PIE_COLORS = ['#10b981', '#0891b2'];
const STATUS_COLORS = {
  'Delivered': '#10b981',
  'Picked Up': '#06b6d4',
  'Ready to Pick': '#6366f1',
  'In Preparation': '#f59e0b',
  'Order Accepted': '#8b5cf6',
  'Order Placed': '#3b82f6',
  'Cancelled by Customer': '#ef4444',
  'Cancelled By Staff': '#f43f5e',
};
const CANCEL_COLORS = ['#ef4444', '#f43f5e', '#f97316', '#eab308', '#8b5cf6', '#06b6d4', '#10b981'];

const StatCard = ({ title, value, sub, color, icon: Icon, testId }) => (
  <Card className={`p-5 border-0 shadow-md ${color || ''}`} data-testid={testId}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs uppercase tracking-wider font-semibold opacity-80">{title}</p>
        <p className="text-2xl font-bold mt-1">{value}</p>
        {sub && <p className="text-xs opacity-70 mt-1">{sub}</p>}
      </div>
      {Icon && <Icon className="w-8 h-8 opacity-30" />}
    </div>
  </Card>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-lg text-xs">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>{p.name}: {typeof p.value === 'number' && p.name?.toLowerCase().includes('rev') ? `$${p.value.toFixed(2)}` : p.value}</p>
      ))}
    </div>
  );
};

export const RevenueReport = () => {
  const { toast } = useToast();
  const [user] = useState(() => {
    try { return JSON.parse(localStorage.getItem('userData') || 'null'); } catch { return null; }
  });
  const [groupBy, setGroupBy] = useState('section');
  const [sections, setSections] = useState([]);
  const [venues, setVenues] = useState([]);
  const [entityId, setEntityId] = useState('');
  const [fromDate, setFromDate] = useState(daysAgoISO(7));
  const [toDate, setToDate] = useState(todayISO());
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);
  const [report, setReport] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    let cancelled = false;
    const pid = user?.entityId;
    if (!pid) { setLoadingMeta(false); return; }
    Promise.all([
      axios.get(`${BACKEND_URL}/api/sections/${pid}`),
      axios.get(`${BACKEND_URL}/api/venues/${pid}`),
    ]).then(([sRes, vRes]) => {
      if (!cancelled) {
        setSections(sRes.data.success ? (sRes.data.sections || []) : []);
        setVenues(vRes.data.success ? (vRes.data.venues || []) : []);
      }
    }).catch(() => {
      if (!cancelled) toast({ title: 'Error', description: 'Failed to load sections/venues', variant: 'destructive' });
    }).finally(() => { if (!cancelled) setLoadingMeta(false); });
    return () => { cancelled = true; };
  }, [user, toast]);

  const switchGroupBy = (val) => { if (val === groupBy) return; setGroupBy(val); setEntityId(''); setReport(null); };

  const generate = async () => {
    if (!user?.entityId) return;
    if (!entityId) { toast({ title: 'Required', description: `Please choose a ${groupBy}.`, variant: 'destructive' }); return; }
    if (fromDate && toDate && fromDate > toDate) { toast({ title: 'Invalid dates', description: '"From" must be on or before "To".', variant: 'destructive' }); return; }
    try {
      setLoadingReport(true);
      const res = await axios.get(`${BACKEND_URL}/api/analytics/revenue/${user.entityId}`, {
        params: { group_by: groupBy, entity_id: entityId, from_date: fromDate || undefined, to_date: toDate || undefined },
      });
      if (res.data.success) { setReport(res.data.data); setActiveTab('overview'); }
      else toast({ title: 'Error', description: res.data.message || 'Failed to load report', variant: 'destructive' });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to load report';
      toast({ title: 'Error', description: typeof msg === 'string' ? msg : 'Failed to load report', variant: 'destructive' });
    } finally { setLoadingReport(false); }
  };

  const entityOptions = groupBy === 'section' ? sections : venues;

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'staff', label: 'Staff Performance' },
    { id: 'orders', label: 'Order Status' },
    { id: 'items', label: 'Top Items' },
    { id: 'allocations', label: 'Allocations' },
  ];

  return (
    <UserLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center space-x-4">
          <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
            <DollarSign className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Revenue Report</h1>
            <p className="text-slate-600 mt-1">Detailed revenue analytics — staff performance, order status, cancellations and trends.</p>
          </div>
        </div>

        {/* Filters */}
        <Card className="p-5 border-0 shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
            <div className="md:col-span-4">
              <label className="block text-xs font-medium text-slate-600 mb-2">Group revenue by</label>
              <div className="flex gap-2">
                {[{ value: 'section', label: 'By Section', icon: Layers }, { value: 'venue', label: 'By Venue', icon: Building2 }].map(opt => {
                  const Icon = opt.icon;
                  const active = groupBy === opt.value;
                  return (
                    <button key={opt.value} type="button" onClick={() => switchGroupBy(opt.value)}
                      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${active ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-600 hover:border-emerald-200'}`}
                      data-testid={`revenue-groupby-${opt.value}`}>
                      <Icon className="w-4 h-4" />{opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="md:col-span-4">
              <label className="block text-xs font-medium text-slate-600 mb-2">Select {groupBy === 'section' ? 'Section' : 'Venue'}</label>
              <select value={entityId} onChange={(e) => setEntityId(e.target.value)} disabled={loadingMeta}
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400 disabled:bg-slate-50"
                data-testid="revenue-entity-select">
                <option value="">{loadingMeta ? 'Loading…' : `Choose a ${groupBy}…`}</option>
                {entityOptions.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-slate-600 mb-2">From date</label>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="text-sm" data-testid="revenue-from-date" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-slate-600 mb-2">To date</label>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="text-sm" data-testid="revenue-to-date" />
            </div>
            <div className="md:col-span-12 flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => { setFromDate(daysAgoISO(7)); setToDate(todayISO()); }} data-testid="revenue-last-7-days">Last 7 days</Button>
              <Button variant="outline" size="sm" onClick={() => { setFromDate(daysAgoISO(30)); setToDate(todayISO()); }} data-testid="revenue-last-30-days">Last 30 days</Button>
              <Button onClick={generate} disabled={loadingReport || loadingMeta} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="revenue-generate-btn">
                {loadingReport ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating…</> : <><RefreshCw className="w-4 h-4 mr-2" />Generate</>}
              </Button>
            </div>
          </div>
        </Card>

        {/* Empty state */}
        {!report && !loadingReport && (
          <Card className="p-12 text-center border-0 shadow-md">
            <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">Pick a {groupBy === 'section' ? 'section' : 'venue'} and date range, then hit Generate.</p>
            <p className="text-slate-400 text-sm mt-1">Revenue = sum of non-cancelled order items in that area.</p>
          </Card>
        )}

        {/* Report */}
        {report && (
          <div className="space-y-5">
            {/* Summary Banner */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-800">{report.entityName} — {report.groupBy === 'section' ? 'Section' : 'Venue'}</h2>
                <p className="text-sm text-slate-500">{report.fromDate} to {report.toDate}</p>
              </div>
            </div>

            {/* Headline Stat Cards Row 1 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="p-5 border-0 shadow-md bg-gradient-to-br from-emerald-500 to-green-600 text-white">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider font-semibold opacity-90">Total Revenue</p>
                    <p className="text-3xl font-bold mt-1" data-testid="revenue-total">{fmt(report.totalRevenue)}</p>
                    <p className="text-xs opacity-80 mt-1">{report.orderCount} orders · {report.itemCount} items</p>
                  </div>
                  <DollarSign className="w-8 h-8 opacity-30" />
                </div>
              </Card>
              <Card className="p-5 border-0 shadow-md">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-orange-600 font-semibold">Kitchen Revenue</p>
                    <p className="text-2xl font-bold text-orange-600 mt-1" data-testid="revenue-kitchen">{fmt(report.kitchenRevenue)}</p>
                    <p className="text-xs text-slate-400 mt-1">{report.totalRevenue > 0 ? ((report.kitchenRevenue / report.totalRevenue) * 100).toFixed(1) : 0}% of total</p>
                  </div>
                  <ChefHat className="w-7 h-7 text-orange-200" />
                </div>
              </Card>
              <Card className="p-5 border-0 shadow-md">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-cyan-600 font-semibold">Bar Revenue</p>
                    <p className="text-2xl font-bold text-cyan-600 mt-1" data-testid="revenue-bar">{fmt(report.barRevenue)}</p>
                    <p className="text-xs text-slate-400 mt-1">{report.totalRevenue > 0 ? ((report.barRevenue / report.totalRevenue) * 100).toFixed(1) : 0}% of total</p>
                  </div>
                  <Wine className="w-7 h-7 text-cyan-200" />
                </div>
              </Card>
              <Card className="p-5 border-0 shadow-md">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-purple-600 font-semibold">Staff Involved</p>
                    <p className="text-2xl font-bold text-purple-600 mt-1" data-testid="revenue-staff-count">{report.totalStaffCount}</p>
                    <p className="text-xs text-slate-400 mt-1">{report.allocations?.length || 0} allocations</p>
                  </div>
                  <Users className="w-7 h-7 text-purple-200" />
                </div>
              </Card>
            </div>

            {/* Headline Stat Cards Row 2 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="p-5 border-0 shadow-md">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-rose-600 font-semibold">Cancelled Revenue</p>
                    <p className="text-2xl font-bold text-rose-600 mt-1" data-testid="revenue-cancelled">{fmt(report.cancelledRevenue)}</p>
                    <p className="text-xs text-slate-400 mt-1">items &amp; orders cancelled</p>
                  </div>
                  <XCircle className="w-7 h-7 text-rose-200" />
                </div>
              </Card>
              <Card className="p-5 border-0 shadow-md">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-amber-600 font-semibold">Void Revenue</p>
                    <p className="text-2xl font-bold text-amber-600 mt-1" data-testid="revenue-void">{fmt(report.voidRevenue)}</p>
                    <p className="text-xs text-slate-400 mt-1">voided items</p>
                  </div>
                  <Tag className="w-7 h-7 text-amber-200" />
                </div>
              </Card>
              <Card className="p-5 border-0 shadow-md">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-indigo-600 font-semibold">Comp / Gesture</p>
                    <p className="text-2xl font-bold text-indigo-600 mt-1" data-testid="revenue-comp">{fmt(report.compRevenue)}</p>
                    <p className="text-xs text-slate-400 mt-1">comped items</p>
                  </div>
                  <Award className="w-7 h-7 text-indigo-200" />
                </div>
              </Card>
              <Card className="p-5 border-0 shadow-md">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-teal-600 font-semibold">Total Discounts</p>
                    <p className="text-2xl font-bold text-teal-600 mt-1" data-testid="revenue-discount">{fmt(report.discountAmount)}</p>
                    <p className="text-xs text-slate-400 mt-1">applied on bills</p>
                  </div>
                  <ArrowUpDown className="w-7 h-7 text-teal-200" />
                </div>
              </Card>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 border-b border-slate-200">
              {tabs.map(t => (
                <button key={t.id} onClick={() => setActiveTab(t.id)}
                  className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === t.id ? 'border-emerald-500 text-emerald-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                  data-testid={`revenue-tab-${t.id}`}>
                  {t.label}
                </button>
              ))}
            </div>

            {/* OVERVIEW TAB */}
            {activeTab === 'overview' && (
              <div className="space-y-5">
                {/* Daily Revenue Trend */}
                <Card className="border-0 shadow-md p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                    <h3 className="font-semibold text-slate-800">Daily Revenue Trend</h3>
                  </div>
                  {report.dailyRevenueTrend?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={report.dailyRevenueTrend} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                        <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={v => `$${v}`} />
                        <Tooltip content={<CustomTooltip />} />
                        <Line type="monotone" dataKey="revenue" name="Revenue" stroke="#10b981" strokeWidth={2.5} dot={{ fill: '#10b981', r: 4 }} activeDot={{ r: 6 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : <p className="text-slate-400 text-sm text-center py-8">No daily data available.</p>}
                </Card>

                {/* Kitchen vs Bar Pie + Order Status Bar */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Card className="border-0 shadow-md p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <BarChart2 className="w-5 h-5 text-orange-500" />
                      <h3 className="font-semibold text-slate-800">Kitchen vs Bar Split</h3>
                    </div>
                    {(report.kitchenRevenue > 0 || report.barRevenue > 0) ? (
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Kitchen', value: report.kitchenRevenue },
                              { name: 'Bar', value: report.barRevenue },
                            ]}
                            cx="50%" cy="50%" outerRadius={75} dataKey="value"
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`}
                            labelLine={false}
                          >
                            {PIE_COLORS.map((c, i) => <Cell key={i} fill={c} />)}
                          </Pie>
                          <Tooltip formatter={(v) => `$${v.toFixed(2)}`} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : <p className="text-slate-400 text-sm text-center py-8">No revenue data.</p>}
                  </Card>

                  <Card className="border-0 shadow-md p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <ShoppingCart className="w-5 h-5 text-indigo-500" />
                      <h3 className="font-semibold text-slate-800">Order Status Distribution</h3>
                    </div>
                    {report.orderStatusBreakdown?.length > 0 ? (
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={report.orderStatusBreakdown} layout="vertical" margin={{ left: 10, right: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                          <XAxis type="number" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                          <YAxis dataKey="status" type="category" tick={{ fontSize: 10, fill: '#64748b' }} width={130} />
                          <Tooltip content={<CustomTooltip />} />
                          <Bar dataKey="count" name="Count" radius={[0, 4, 4, 0]}>
                            {report.orderStatusBreakdown.map((entry, i) => (
                              <Cell key={i} fill={STATUS_COLORS[entry.status] || '#6366f1'} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : <p className="text-slate-400 text-sm text-center py-8">No order data.</p>}
                  </Card>
                </div>

                {/* Cancellation Reasons */}
                {report.cancellationReasons?.length > 0 && (
                  <Card className="border-0 shadow-md p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <XCircle className="w-5 h-5 text-rose-500" />
                      <h3 className="font-semibold text-slate-800">Cancellation Breakdown</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={report.cancellationReasons} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="reason" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                        <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="count" name="Count" radius={[4, 4, 0, 0]}>
                          {report.cancellationReasons.map((_, i) => <Cell key={i} fill={CANCEL_COLORS[i % CANCEL_COLORS.length]} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Card>
                )}
              </div>
            )}

            {/* STAFF PERFORMANCE TAB */}
            {activeTab === 'staff' && (
              <Card className="border-0 shadow-md overflow-hidden" data-testid="staff-performance-table">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-purple-600" />
                    <h2 className="font-semibold text-slate-800">Staff Performance</h2>
                  </div>
                  <span className="text-xs text-slate-500">{report.staffPerformance?.length || 0} staff member(s)</span>
                </div>
                {!report.staffPerformance?.length ? (
                  <div className="p-10 text-center">
                    <AlertCircle className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No staff data available for this period.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-slate-500">
                        <tr>
                          <th className="px-5 py-3 text-left font-medium">#</th>
                          <th className="px-5 py-3 text-left font-medium">Staff Name</th>
                          <th className="px-5 py-3 text-left font-medium">Role</th>
                          <th className="px-5 py-3 text-center font-medium">Allocations</th>
                          <th className="px-5 py-3 text-center font-medium">Orders</th>
                          <th className="px-5 py-3 text-right font-medium">Revenue</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {report.staffPerformance.map((s, i) => (
                          <tr key={s.staffId} className="hover:bg-slate-50" data-testid={`staff-row-${i}`}>
                            <td className="px-5 py-3 text-slate-400 text-xs">{i + 1}</td>
                            <td className="px-5 py-3 font-medium text-slate-700">{s.name}</td>
                            <td className="px-5 py-3">
                              <span className="px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-700">{s.role || 'Staff'}</span>
                            </td>
                            <td className="px-5 py-3 text-center text-slate-600">{s.allocationCount}</td>
                            <td className="px-5 py-3 text-center text-slate-600">{s.orderCount}</td>
                            <td className="px-5 py-3 text-right font-semibold text-emerald-700">{fmt(s.revenue)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {/* Staff Revenue Bar Chart */}
                {report.staffPerformance?.length > 0 && (
                  <div className="p-5 border-t border-slate-100">
                    <p className="text-xs font-medium text-slate-600 mb-3">Revenue Contribution by Staff</p>
                    <ResponsiveContainer width="100%" height={Math.max(150, report.staffPerformance.length * 40)}>
                      <BarChart data={report.staffPerformance} layout="vertical" margin={{ left: 10, right: 30 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 10, fill: '#94a3b8' }} tickFormatter={v => `$${v}`} />
                        <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: '#64748b' }} width={120} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="revenue" name="Revenue" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </Card>
            )}

            {/* ORDER STATUS TAB */}
            {activeTab === 'orders' && (
              <div className="space-y-5">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {report.orderStatusBreakdown?.map((s, i) => (
                    <Card key={i} className="p-4 border-0 shadow-md" data-testid={`order-status-card-${i}`}>
                      <p className="text-xs font-medium text-slate-500 mb-1">{s.status}</p>
                      <p className="text-2xl font-bold" style={{ color: STATUS_COLORS[s.status] || '#6366f1' }}>{s.count}</p>
                      <p className="text-xs text-slate-400 mt-0.5">order(s)</p>
                    </Card>
                  ))}
                </div>
                <Card className="border-0 shadow-md p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <XCircle className="w-5 h-5 text-rose-500" />
                    <h3 className="font-semibold text-slate-800">Cancellation Reasons Detail</h3>
                  </div>
                  {report.cancellationReasons?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-500">
                          <tr>
                            <th className="px-5 py-3 text-left font-medium">Reason</th>
                            <th className="px-5 py-3 text-right font-medium">Count</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {report.cancellationReasons.map((r, i) => (
                            <tr key={i} className="hover:bg-slate-50">
                              <td className="px-5 py-3 text-slate-700">{r.reason}</td>
                              <td className="px-5 py-3 text-right font-semibold text-rose-600">{r.count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : <p className="text-slate-400 text-sm text-center py-8">No cancellations in this period.</p>}
                </Card>
              </div>
            )}

            {/* TOP ITEMS TAB */}
            {activeTab === 'items' && (
              <Card className="border-0 shadow-md overflow-hidden" data-testid="top-items-table">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ChefHat className="w-5 h-5 text-orange-500" />
                    <h2 className="font-semibold text-slate-800">Top Menu Items</h2>
                  </div>
                  <span className="text-xs text-slate-500">by revenue</span>
                </div>
                {!report.topMenuItems?.length ? (
                  <div className="p-10 text-center">
                    <AlertCircle className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No item data for this period.</p>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-500">
                          <tr>
                            <th className="px-5 py-3 text-left font-medium">#</th>
                            <th className="px-5 py-3 text-left font-medium">Item Name</th>
                            <th className="px-5 py-3 text-left font-medium">Type</th>
                            <th className="px-5 py-3 text-center font-medium">Qty Sold</th>
                            <th className="px-5 py-3 text-right font-medium">Revenue</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {report.topMenuItems.map((item, i) => (
                            <tr key={i} className="hover:bg-slate-50" data-testid={`top-item-row-${i}`}>
                              <td className="px-5 py-3 text-slate-400 text-xs">{i + 1}</td>
                              <td className="px-5 py-3 font-medium text-slate-700">{item.name}</td>
                              <td className="px-5 py-3">
                                <span className={`px-2 py-0.5 rounded-full text-xs ${item.orderType === 'Drink' ? 'bg-cyan-100 text-cyan-700' : 'bg-orange-100 text-orange-700'}`}>
                                  {item.orderType === 'Drink' ? 'Bar' : 'Kitchen'}
                                </span>
                              </td>
                              <td className="px-5 py-3 text-center text-slate-600">{item.quantity}</td>
                              <td className="px-5 py-3 text-right font-semibold text-emerald-700">{fmt(item.revenue)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="p-5 border-t border-slate-100">
                      <p className="text-xs font-medium text-slate-600 mb-3">Top 10 Items by Revenue</p>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={report.topMenuItems} margin={{ top: 5, right: 20, left: 0, bottom: 60 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94a3b8' }} angle={-30} textAnchor="end" interval={0} />
                          <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickFormatter={v => `$${v}`} />
                          <Tooltip content={<CustomTooltip />} />
                          <Bar dataKey="revenue" name="Revenue" radius={[4, 4, 0, 0]}>
                            {report.topMenuItems.map((item, i) => (
                              <Cell key={i} fill={item.orderType === 'Drink' ? '#0891b2' : '#f97316'} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </>
                )}
              </Card>
            )}

            {/* ALLOCATIONS TAB */}
            {activeTab === 'allocations' && (
              <Card className="border-0 shadow-md overflow-hidden" data-testid="allocations-table">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                  <h2 className="font-semibold text-slate-800">Top Contributing Allocations</h2>
                  <span className="text-xs text-slate-500">{report.allocations?.length} allocation(s)</span>
                </div>
                {!report.allocations?.length ? (
                  <div className="p-10 text-center">
                    <AlertCircle className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No revenue in this period for the selected {report.groupBy}.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-slate-500">
                        <tr>
                          <th className="px-5 py-3 text-left font-medium">Guest</th>
                          <th className="px-5 py-3 text-left font-medium">Room</th>
                          <th className="px-5 py-3 text-left font-medium">F&B Manager</th>
                          <th className="px-5 py-3 text-left font-medium">Attendants</th>
                          <th className="px-5 py-3 text-left font-medium">F&B Servers</th>
                          <th className="px-5 py-3 text-center font-medium">Adults/Kids</th>
                          <th className="px-5 py-3 text-center font-medium">Orders</th>
                          <th className="px-5 py-3 text-right font-medium">Revenue</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {report.allocations.map((a, i) => (
                          <tr key={a.allocationId} className="hover:bg-slate-50" data-testid={`revenue-alloc-row-${i}`}>
                            <td className="px-5 py-3 font-medium text-slate-700">{a.guestName || '—'}</td>
                            <td className="px-5 py-3 text-slate-500">{a.roomNumber || '—'}</td>
                            <td className="px-5 py-3 text-slate-600">{a.fbManager || '—'}</td>
                            <td className="px-5 py-3 text-slate-600">
                              {a.attendants?.length > 0 ? a.attendants.join(', ') : '—'}
                            </td>
                            <td className="px-5 py-3 text-slate-600">
                              {a.fbServers?.length > 0 ? a.fbServers.join(', ') : '—'}
                            </td>
                            <td className="px-5 py-3 text-center text-slate-600">
                              {a.numAdults || 0}/{a.numKids || 0}
                            </td>
                            <td className="px-5 py-3 text-center text-slate-600">{a.orderCount}</td>
                            <td className="px-5 py-3 text-right font-semibold text-emerald-700">{fmt(a.revenue)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            )}
          </div>
        )}
      </div>
    </UserLayout>
  );
};

export default RevenueReport;
